import { NOMS_DES_COULEURS } from '@nextilt/commun';
import type { DateAuPlusTot } from '../api/types';
import { enFrancais, enFrancaisAvecAnnee, jourEnChiffres, selonLaDateAuPlusTot } from '../datesEnMots';
import type { TriDesProjets } from '../lesProjets';
import { auMilieu } from './accords';
import { MES_PROJETS } from './cadre';
import { PAS_DE_DATE, PAS_ENCORE_CALCULABLE } from './etats';
import { TOUS_LES_PROJETS } from './maintenant';

/**
 * La page Projets (chantier `revue-design`, ticket 05, maquette `design/projets.html`, décisions
 * d'Igor du 2026-10-03, « Projets » 1 et 2) : les tris, les trois chemins d'un nouveau projet,
 * les rayons de l'étagère (chantier etagere-des-projets), « Continuer », le menu « … ».
 * « Par échéance » remplace « Ce qui presse » (calibration, réponses 4 et 5).
 */
export const PERSONNE_N_A_DONNE_DE_DELAI = 'personne n’a donné de délai';
/** Dans l'ordre de la maquette de l'étagère : Récents, Par échéance, A à Z. */
export const TRIS_DES_PROJETS: [TriDesProjets, string][] = [
  ['recents', 'Récents'],
  ['echeance', 'Par échéance'],
  ['a_z', 'A à Z'],
];
export const RANGER_LES_PROJETS = 'Ranger les projets';
export const NOUVEAU_PROJET = 'Nouveau projet';
export const PARTIR_D_UN_MODELE = 'Partir d’un modèle';
export const IMPORTER_UN_PLAN_D_IA = 'Importer un plan d’IA';
export const NOM_DU_NOUVEAU_PROJET = 'Nom du nouveau projet';
export const EXEMPLE_DE_PROJET = 'Acheter la maison';
export const CREER = 'Créer';
/**
 * Le + du téléphone selon la page (chantier `telephone-projets-fiche`, ticket 05, maquette
 * `design/telephone.html`, « Le + dans l'Agenda » et « Nouveau projet ») : « projet », jamais
 * « dossier ». Ni Rappel, ni étape à choisir.
 */
export const MOTS_DU_PLUS = {
  /** Les sortes, dans l'ordre où elles se présentent. */
  sortes: { tache: 'Tâche', idee: 'Idée', rendez_vous: 'Rendez-vous', echeance: 'Échéance' },
  ajouterDans: (titre: string) => `Ajouter dans ${titre}`,
  ajouterAuJour: (jour: string) => `Ajouter au ${enFrancais(jour, true)}`,
  ajouterATaJournee: 'Ajouter à ta journée',
  ajouterUneTache: 'Ajouter une tâche',
  champ: 'Ce à quoi tu viens de penser',
  ajouter: 'Ajouter',
  fermer: 'Fermer',
  leProjet: 'Le projet',
  /** La Tâche de l'Agenda entre dans la journée de ce jour. */
  dansTaJourneeDu: (jour: string) => `Dans ta journée du ${enFrancais(jour, true)}`,
  /** L'Échéance : une tâche à faire avant ce jour. */
  avantLe: (jour: string) => `Avant le ${enFrancais(jour)}`,
  heure: 'À',
  heureDuRendezVous: 'L’heure du rendez-vous',
  ouParmiLesPossibles: 'Ou choisis parmi ce qui est possible aujourd’hui',
  aucunPossible: 'Rien d’autre de possible aujourd’hui.',
  ilFautLeReseau: 'Il faut le réseau',
  sansReseau: 'Sans réseau : une tâche ou une idée, qui partira au retour du réseau.',
  sansReseauPasDansLaJournee: 'Sans réseau : elle partira dans son projet au retour du réseau, sans entrer dans la journée.',
  pasDansLaJournee: (titre: string, raison: string) => `« ${titre} » est dans son projet, pas dans la journée : ${raison}`,
  dAbordUnProjet: 'Pour ajouter quelque chose, il faut d’abord un projet.',
  saCouleur: 'Sa couleur',
  /** Les noms des cinq couleurs, écrits dans `@nextilt/commun` (le MCP les dit aussi). */
  couleurs: NOMS_DES_COULEURS,
  partirDUnModele: `${PARTIR_D_UN_MODELE} ›`,
  importerUnPlan: 'Importer un plan écrit par une IA ›',
};
export const SUPPRIMER_LE_PROJET = 'Supprimer le projet';
export const GARDER_LE_PROJET = 'Garder le projet';
export const PROJET_PAS_SUPPRIME = 'Le projet n’a pas pu être supprimé. Il est toujours là, rien n’a bougé.';
/*
 * « Projet » au lieu de « dossier » au téléphone (chantier `telephone-projets-fiche`, ticket 06 ;
 * défaut accepté par Igor le 2026-10-05, décision 20 de `telephone-refonte`). L'ordinateur garde
 * ses mots jusqu'au chantier en attente `les-mots` : ceux que les deux partagent sont ici côte à
 * côte, et `motsDuDossier(auPouce)` choisit. Les morceaux de phrase gardent leur place dans le
 * texte de l'écran (ses espaces insécables, ses liens).
 */
const DIT_DOSSIER = {
  // Les pages sans projet (`Premier.tsx`, `ProjetsVides.tsx`).
  unDossierCEst: 'Un dossier, c’est n’importe quoi qui prend plus d’une étape.',
  ouvrirUn: 'Ouvrir un dossier',
  ilExiste: 'C’est tout. Pas de date, pas de catégorie, pas de description. Le dossier existe.',
  desTonPremier: 'Dès ton premier dossier, cette page te dit quoi faire tout de suite, selon ton temps et où tu es.',
  dansUnNouveau: 'dans un nouveau dossier',
  aMoitieVide: 'dossier à moitié vide reste utile.',
  iciViendront: 'Ici viendront tes dossiers, tous au même endroit.',
  exemplesDe: 'Exemples de dossiers',
  uneLignePar: 'Une ligne par dossier',
  tonPremier: 'Ton premier dossier',
  tousLes: 'Tous les dossiers',
  // Déplacer (`Deplacer.tsx`).
  mesDossiers: 'Mes dossiers',
  aucun: 'Aucun dossier',
  sortirComme: (titre: string) => `Sortir « ${titre} » comme dossier`,
  deviendraUn: 'elle deviendra un dossier avec sa propre couleur.',
  // Poser une condition, et la boucle refusée (`Dependances.tsx`, `RefusDeCycle.tsx`).
  unDossier: 'Un dossier',
  tousLesEnMinuscules: 'tous les dossiers',
  unSeul: 'Celle-ci tient dans un seul dossier',
  // Supprimer depuis la fiche, l'inventaire (`SupprimerBulle.tsx`, `Inventaire.tsx`).
  garder: 'Garder le dossier',
  pasSupprime: 'Le dossier n’a pas pu être supprimé. Il est toujours là, rien n’a bougé.',
  decompte: 'Ce que ça change au décompte du dossier',
  // Le modèle et l'import d'un plan (`Modele.tsx`, `ImporterPlan.tsx`).
  poseDansTon: 'Ce qu’il pose dans ton dossier',
  ecritALaMain: 'un dossier écrit à la main',
  partirDUnVide: 'Partir d’un dossier vide',
  enDeux: 'découpe-le en deux dossiers.',
  nouveauALaRacine: 'Un nouveau dossier apparaît à la racine.',
  // Une bulle qui n'existe plus, l'entrée (`Feuille.tsx`, `Connexion.tsx`).
  revenirAux: 'Revenir aux dossiers',
  tesDossiers: 'Tes dossiers',
};
const DIT_PROJET: typeof DIT_DOSSIER = {
  unDossierCEst: 'Un projet, c’est n’importe quoi qui prend plus d’une étape.',
  ouvrirUn: 'Ouvrir un projet',
  ilExiste: 'C’est tout. Pas de date, pas de catégorie, pas de description. Le projet existe.',
  desTonPremier: 'Dès ton premier projet, cette page te dit quoi faire tout de suite, selon ton temps et où tu es.',
  dansUnNouveau: 'dans un nouveau projet',
  aMoitieVide: 'projet à moitié vide reste utile.',
  iciViendront: 'Ici viendront tes projets, tous au même endroit.',
  exemplesDe: 'Exemples de projets',
  uneLignePar: 'Une ligne par projet',
  tonPremier: 'Ton premier projet',
  tousLes: TOUS_LES_PROJETS,
  mesDossiers: MES_PROJETS,
  aucun: 'Aucun projet',
  sortirComme: (titre: string) => `Sortir « ${titre} » comme projet`,
  deviendraUn: 'elle deviendra un projet avec sa propre couleur.',
  unDossier: 'Un projet',
  tousLesEnMinuscules: 'tous les projets',
  unSeul: 'Celle-ci tient dans un seul projet',
  garder: GARDER_LE_PROJET,
  pasSupprime: PROJET_PAS_SUPPRIME,
  decompte: 'Ce que ça change au décompte du projet',
  poseDansTon: 'Ce qu’il pose dans ton projet',
  ecritALaMain: 'un projet écrit à la main',
  partirDUnVide: 'Partir d’un projet vide',
  enDeux: 'découpe-le en deux projets.',
  nouveauALaRacine: 'Un nouveau projet apparaît à la racine.',
  revenirAux: 'Revenir aux projets',
  tesDossiers: 'Tes projets',
};
/** Les mots qui disaient « dossier » : « projet » au téléphone, ceux d'avant à l'ordinateur. */
export function motsDuDossier(auPouce: boolean): typeof DIT_DOSSIER {
  return auPouce ? DIT_PROJET : DIT_DOSSIER;
}
/*
 * Tous les projets, au téléphone (chantier `telephone-projets-fiche`, ticket 01, maquette
 * `design/telephone.html`, piste « Projets ») : une tuile par projet.
 */
/** Sous le nombre en haut à droite de la tuile : « possibles », « possible » pour 0 ou 1. */
export function motPossibles(combien: number): string {
  return combien > 1 ? 'possibles' : 'possible';
}
/** L'étiquette de l'encart quand une chose est à faire. */
export const A_TOI = 'À toi';
/** L'étiquette de l'encart quand rien n'est possible et que le monde doit répondre. */
export const ON_ATTEND = 'On attend';
/** « Une réponse du monde », « 3 réponses du monde ». */
export function reponsesDuMonde(combien: number): string {
  return combien > 1 ? `${combien} réponses du monde` : 'Une réponse du monde';
}
/** « Au plus tôt le 14 octobre 2026 », sous ce qu'on attend. */
export function auPlusTotLe(jour: string): string {
  return `Au plus tôt le ${enFrancaisAvecAnnee(jour)}`;
}
export const RIEN_A_FAIRE_ICI = 'Rien à faire ici pour l’instant';
export const TUILE_TOUT_EST_FAIT = 'Tout est fait';
export const TUILE_RIEN_DEDANS = 'Rien dedans pour l’instant';
/* Un projet et une étape, au téléphone (chantier telephone-projets-fiche, ticket 02). */
export const ICI_TU_PEUX_FAIRE = 'Ici, tu peux faire';
/** « Ici, tu peux faire · 22 », la ligne repliée. */
export function iciTuPeuxFaire(combien: number): string {
  return `${ICI_TU_PEUX_FAIRE} · ${combien}`;
}
export function autresActionsPour(titre: string): string {
  return `Autres actions pour ${titre}`;
}
export function continuerDans(titre: string): string {
  return `Continuer : ouvrir « ${titre} »`;
}
/*
 * L'étagère des projets (chantier etagere-des-projets, maquette du carnet `projets.html`) : les
 * deux rayons, et la dernière ligne d'un carnet quand rien n'attend le monde.
 */
export const RAYON_EN_COURS = 'En cours';
export const RAYON_FINIS = 'Finis';
/*
 * Un projet ouvert au téléphone, en arborescence (chantier telephone-projet, 02, maquette
 * `app-au-telephone.html`, `projet()`).
 */
/** « + une bulle dans « Devis du plombier » », en bas d'une branche dépliée. */
export function uneBulleDans(titre: string): string {
  return `+ une bulle dans « ${titre} »`;
}
export const UNE_BRANCHE = '+ une branche';
/** « 4/8 » : faites sur le total, au bout d'un carnet et d'une branche (jamais un pourcentage). */
export function faitesSurTotal(faites: number, total: number): string {
  return `${faites}/${total}`;
}
export const CE_QUI_PRESSE = 'Ce qui presse';
/** « avant le 30/10 », au bout d'un fichier de l'arbre. */
export function avantLeCourt(jour: string): string {
  return `avant le ${jourEnChiffres(jour)}`;
}
/** « réponse le 25/10 » : le jour où le monde doit répondre. */
export function reponseLe(jour: string): string {
  return `réponse le ${jourEnChiffres(jour)}`;
}
/** « Aller au magasin, marge de 2 jours » : une ligne de Prioritaire sur le post-it. */
export function ceQuiPresseLigne(titre: string, pourquoi: string | null): string {
  return pourquoi ? `${titre}, ${pourquoi.charAt(0).toLocaleLowerCase('fr-FR')}${pourquoi.slice(1)}` : titre;
}
/** « Attend le monde : Recevoir le devis, réponse le 25/10 ». */
export function attendLeMondeLigne(titre: string, quand: string | null): string {
  return `Attend le monde : ${titre}${quand ? `, ${quand}` : ''}`;
}
/* Le détail d'une branche au téléphone (chantier telephone-projet, 03). */
/** « + un détail dans « Devis du plombier » », en dernière ligne du paquet. */
export function unDetailDans(titre: string): string {
  return `+ un détail dans « ${titre} »`;
}
/** Après « 1/2 » sur l'intercalaire d'une branche qui attend le monde. */
export const ATTEND_LE_MONDE_EN_MENTION = ' · attend le monde';
/** La ligne grise d'une tâche retenue : « attend « Choisir le carrelage » ». */
export function attendLaTache(titre: string): string {
  return `attend « ${titre} »`;
}
export const NOTE_DE_LA_BRANCHE = 'Note de la branche';
/** Le ⚙ d'un projet ouvert au téléphone. */
export const MONTRER_CE_QUI_EST_FAIT = 'Montrer ce qui est fait';
export const BARRE_A_SA_PLACE = 'barré, à sa place';
export const RENOMMER_COULEUR_DU_PROJET = 'Renommer, couleur du projet';
/** Combien de carnets au rayon « Finis » du téléphone : « 1 carnet », « 2 carnets ». */
export function combienDeCarnets(combien: number): string {
  return `${combien} ${combien > 1 ? 'carnets' : 'carnet'}`;
}
/** Les carnets de face, téléphone couché : « 5 en cours », « 2 finis ». */
export function combienEnCours(combien: number): string {
  return `${combien} en cours`;
}
export function combienDeFinis(combien: number): string {
  return `${combien} ${combien > 1 ? 'finis' : 'fini'}`;
}
/** « Fin au plus tôt : 14 octobre 2026 », « Fin au plus tôt : pas de date ». */
export function finAuPlusTotDuCarnet(quand: string): string {
  return `Fin au plus tôt : ${quand}`;
}
/** « Fin au plus tôt » : « 15 octobre 2026 », « pas de date », « pas encore calculable ». */
export function finAuPlusTot(date: DateAuPlusTot): string {
  return selonLaDateAuPlusTot(date, {
    date: enFrancaisAvecAnnee,
    quandTuVeux: auMilieu(PAS_DE_DATE),
    pasCalculable: PAS_ENCORE_CALCULABLE,
  });
}
/** Le téléphone couché, dans un projet (chantier telephone-couche) : ses deux vues, et l'encadré qui l'annonce. */
export const ANALYSE_COUCHEE = 'Analyse';
export const HIERARCHIE_COUCHEE = 'Hiérarchie';
export const LES_VUES_COUCHEES = 'Les vues du projet couché';
/** L'Analyse couchée : « 3/8 faites ». */
export const FAITES = 'faites';
export const TOURNE_TON_TELEPHONE = 'Tourne ton téléphone : l’analyse et la hiérarchie du projet.';
